import { useState } from 'react';

/**
 * La courbe de production du jour d'une centrale photovoltaïque.
 *
 * POURQUOI CE COMPOSANT EXISTE
 *
 * David, le 08/09 : « la courbe de production de la journée, le même graphique que sur ton
 * écran S-Miles ». Ces 65 à 96 points arrivaient déjà dans la réponse de l'API cinq fois
 * par heure, et le serveur n'en gardait que le dernier — la série était téléchargée puis
 * jetée. Elle traverse désormais jusqu'ici.
 *
 * ⚠️ LES HEURES SONT CELLES DE LA CENTRALE, NON CONVERTIES. Chaque point porte son
 * horodatage tel que Hoymiles l'écrit. Le convertir demanderait le fuseau de la centrale,
 * et se tromper de fuseau décalerait la courbe entière sans que rien ne le signale — le
 * même défaut déjà payé sur la fraîcheur du dernier point. Affichée brute, elle reste
 * vérifiable ligne à ligne dans S-Miles.
 *
 * UNE SEULE SÉRIE, DONC PAS DE LÉGENDE : le titre la nomme. Le dernier point est marqué,
 * parce que c'est celui que les autres chiffres du panneau commentent.
 */

const LARGEUR = 320;
const HAUTEUR = 84;
const MARGE_BAS = 14;   // la place des heures
const MARGE_HAUT = 10;  // la place du pic, pour qu'il ne touche pas le bord

/** L'heure seule, telle que la centrale l'écrit : « 2026-09-10 08:15 » -> « 08:15 ». */
function heure(instant) {
  return String(instant || '').slice(11, 16);
}

export default function CourbeProductionPv({ points = [] }) {
  const [survol, setSurvol] = useState(null);

  // Deux points au moins : une ligne entre un seul point n'existe pas, et un tracé à
  // point unique se lirait comme une journée plate alors qu'on n'a qu'une mesure.
  if (!Array.isArray(points) || points.length < 2) return null;

  const valeurs = points.map((p) => Number(p.puissance_w) || 0);
  const maxi = Math.max(...valeurs);
  // Une journée entièrement à zéro (nuit, onduleur coupé) est une information, mais pas
  // une courbe : on évite la division par zéro et on aplatit sur la ligne de base.
  const echelle = maxi > 0 ? maxi : 1;
  const utile = HAUTEUR - MARGE_BAS - MARGE_HAUT;

  const x = (i) => (i / (points.length - 1)) * LARGEUR;
  const y = (v) => MARGE_HAUT + utile - (v / echelle) * utile;

  const ligne = points.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(valeurs[i]).toFixed(1)}`).join(' ');
  const aire = `${ligne} L${LARGEUR},${HAUTEUR - MARGE_BAS} L0,${HAUTEUR - MARGE_BAS} Z`;

  const dernier = points.length - 1;
  const vu = survol == null ? null : points[survol];

  // Trois repères d'heure : début, milieu, fin. Chacun nomme une heure que la courbe
  // atteint réellement — jamais une graduation ronde inventée.
  const reperes = [0, Math.floor(dernier / 2), dernier];

  return (
    <div className="pt-2 mt-2 border-t border-amber-100 dark:border-amber-900/40">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider text-neutral-400">
          Production du jour
        </span>
        <span className="text-[10px] text-neutral-400 tabular-nums">
          pic {Math.round(maxi)} W
        </span>
      </div>

      <svg
        viewBox={`0 0 ${LARGEUR} ${HAUTEUR}`}
        className="w-full h-[84px] overflow-visible"
        role="img"
        aria-label={`Courbe de production du jour, ${points.length} points, pic à ${Math.round(maxi)} watts`}
        onMouseLeave={() => setSurvol(null)}
      >
        <defs>
          <linearGradient id="remplissage-pv" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" className="[stop-color:#B45309] dark:[stop-color:#FBBF24]" stopOpacity="0.22" />
            <stop offset="100%" className="[stop-color:#B45309] dark:[stop-color:#FBBF24]" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* La ligne de base, discrète : elle situe le zéro sans concurrencer la courbe. */}
        <line
          x1="0" y1={HAUTEUR - MARGE_BAS} x2={LARGEUR} y2={HAUTEUR - MARGE_BAS}
          className="stroke-neutral-200 dark:stroke-neutral-700"
          strokeWidth="1" vectorEffect="non-scaling-stroke"
        />

        <path d={aire} fill="url(#remplissage-pv)" />
        <path
          d={ligne} fill="none"
          className="stroke-amber-700 dark:stroke-amber-400"
          strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />

        {/* Le dernier point, marqué : c'est celui que les chiffres du panneau commentent. */}
        <circle
          cx={x(dernier)} cy={y(valeurs[dernier])} r="3"
          className="fill-amber-700 dark:fill-amber-400 stroke-white dark:stroke-neutral-900"
          strokeWidth="1.5"
        />

        {/* Le viseur de survol. */}
        {vu && (
          <g>
            <line
              x1={x(survol)} y1={MARGE_HAUT - 4} x2={x(survol)} y2={HAUTEUR - MARGE_BAS}
              className="stroke-neutral-300 dark:stroke-neutral-600"
              strokeWidth="1" vectorEffect="non-scaling-stroke"
            />
            <circle
              cx={x(survol)} cy={y(valeurs[survol])} r="3.5"
              className="fill-amber-700 dark:fill-amber-400 stroke-white dark:stroke-neutral-900"
              strokeWidth="1.5"
            />
          </g>
        )}

        {reperes.map((i) => (
          <text
            key={i}
            x={Math.min(Math.max(x(i), 12), LARGEUR - 12)}
            y={HAUTEUR - 3}
            textAnchor={i === 0 ? 'start' : i === dernier ? 'end' : 'middle'}
            className="fill-neutral-400 text-[9px]"
          >
            {heure(points[i].instant_centrale)}
          </text>
        ))}

        {/* Les zones de survol : plus larges que les marques, pour être atteignables. */}
        {points.map((p, i) => (
          <rect
            key={i}
            x={x(i) - LARGEUR / points.length / 2}
            y="0"
            width={LARGEUR / points.length}
            height={HAUTEUR - MARGE_BAS}
            fill="transparent"
            onMouseEnter={() => setSurvol(i)}
          />
        ))}
      </svg>

      {/* La valeur survolée, sous la courbe : elle ne masque jamais le tracé. */}
      <p className="text-[10px] text-neutral-500 dark:text-neutral-400 tabular-nums h-[14px]">
        {vu
          ? `${heure(vu.instant_centrale)} — ${Math.round(Number(vu.puissance_w) || 0)} W`
          : `${points.length} points, au pas de 15 minutes`}
      </p>
    </div>
  );
}
